import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Platform } from 'react-native';
import { useSitesWithinRadius } from './useSitesWithinRadius';
import { ForNeighborsError } from './classifyForNeighborsError';
import { useResolvedUserLocation, getZipCodeLocation, LOCAL_AREA_DELTA } from './useResolvedUserLocation';
import { regionToSearchParams } from './regionToSearchParams';
// eslint-disable-next-line import/extensions
import { makeMapViewRegion } from './MapView'; // platform-specific: MapView.native.js / MapView.web.js
import pantryRegionForUser from '../../utils/pantryRegionForUser';
import { Coordinate, Site } from '../../types/Site';

export interface SiteMapUser {
  latitude?: number;
  longitude?: number;
  zip_code?: string;
}

export interface UseSiteMapRegionAndSitesOptions {
  enabled?: boolean;
}

export interface UseSiteMapRegionAndSitesResult {
  mapRegion: any;
  setMapRegion: (region: any) => void;
  sites: Site[];
  loading: boolean;
  error: ForNeighborsError | null;
  resultsTruncated: boolean;
  refetch: () => void;
  recenter: () => void;
  // The user's actual resolved location (GPS/ZIP), distinct from mapRegion -- which also tracks
  // the user's own panning/zooming, so it stops representing "where the user is" as soon as they
  // move the map. Used as SiteDetailsView's userCoordinate for a real distance figure; see
  // useSite.ts's distanceMiles and the TODO on ForNeighborsApi.ts's describeSite.
  userCoordinate: Coordinate | null;
}

// Shared by any screen embedding the Sites map/list (currently SiteMapPantriesContent, used by
// PantriesScreen.jsx's Statsig-gated path) so the region-state + fetching logic isn't duplicated.
export function useSiteMapRegionAndSites(
  user: SiteMapUser | undefined,
  isLoggedIn: boolean,
  targetZipCode?: string,
  options: UseSiteMapRegionAndSitesOptions = {}
): UseSiteMapRegionAndSitesResult {
  const { enabled = true } = options;
  // The initial region, computed once from the user's profile -- mapRegion (below) starts here and
  // then tracks the user's panning/zooming from that point on.
  const nativeRegion = useMemo(() => pantryRegionForUser(user), [user]);
  const [mapRegion, setMapRegion] = useState(() => makeMapViewRegion(nativeRegion));

  // Resolves the user's real location (GPS, falling back to their ZIP code) and re-centers the map
  // on it once available -- pantryRegionForUser above is only the synchronous placeholder shown
  // while this resolves. Matches PantryMap.js's centerMap() behavior.
  const { region: resolvedLocationRegion, recenter } = useResolvedUserLocation(user?.zip_code);

  // Both this hook's own effect below and the target-ZIP effect call setMapRegion asynchronously
  // with no inherent ordering -- whichever resolves second would otherwise win, silently breaking
  // the "ZIP override takes priority" guarantee if GPS (which can involve a permission prompt)
  // happens to resolve after the ZIP geocode. targetZipAppliedRef tracks whether that override has
  // won so far, so GPS's *automatic* resolution defers to it; recenterRequestedRef tracks an
  // explicit tap of the recenter button, which must always win regardless of ZIP override history.
  const targetZipAppliedRef = useRef(false);
  const recenterRequestedRef = useRef(false);

  useEffect(() => {
    if (resolvedLocationRegion && (recenterRequestedRef.current || !targetZipAppliedRef.current)) {
      setMapRegion(makeMapViewRegion(resolvedLocationRegion));
    }
  }, [resolvedLocationRegion]);

  // Deep-link override: a target ZIP code (e.g. a shared search link) takes priority over the
  // user's own location, resolved once on mount -- mirrors PantryMap.js's centerMap(targetZipCode)
  // mount effect, which is deliberately a one-time override rather than reactive to later changes.
  useEffect(() => {
    if (!targetZipCode) return undefined;
    let cancelled = false;
    (async () => {
      const location = await getZipCodeLocation(targetZipCode);
      if (!cancelled && location) {
        targetZipAppliedRef.current = true;
        setMapRegion(
          makeMapViewRegion({
            latitude: location.coords.latitude,
            longitude: location.coords.longitude,
            ...LOCAL_AREA_DELTA,
          })
        );
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deliberately mount-only, matching PantryMap.js's centerMap(targetZipCode) effect
  }, []);

  const wrappedRecenter = useCallback(() => {
    recenterRequestedRef.current = true;
    recenter();
  }, [recenter]);

  const { center, radiusMeters } = useMemo(() => {
    const rawNativeRegion = mapRegion.asNativeRegion();
    // MapViewWeb's asNativeRegion() computes a center-to-edge half-span when it has real map
    // bounds (mapImpl set, populated once the map's onIdle has fired at least once), while
    // regionToSearchParams expects a full edge-to-edge span. Doubling here, scoped to web, rather
    // than fixing MapViewWeb itself -- that method is shared with the shipped PantryMap.js, whose
    // zoom-helper threshold and search radius are already calibrated against its current
    // (mislabeled) half-span output; changing it would silently alter that live behavior, which
    // needs its own dedicated review.
    //
    // Before the first onIdle -- initial mount, right after GPS/ZIP resolution, or right after an
    // explicit recenter, all of which construct a fresh MapViewWebRegion with mapImpl still null
    // -- asNativeRegion() instead falls back to reconstructing the delta from the zoom level,
    // which round-trips the already-full-span delta it was originally derived from. Doubling that
    // would overstate the search radius by ~2x, so only double once real bounds are available.
    const searchNativeRegion =
      Platform.OS === 'web' && mapRegion.mapImpl
        ? {
            ...rawNativeRegion,
            latitudeDelta: rawNativeRegion.latitudeDelta * 2,
            longitudeDelta: rawNativeRegion.longitudeDelta * 2,
          }
        : rawNativeRegion;
    return regionToSearchParams(searchNativeRegion);
  }, [mapRegion]);
  const { sites, loading, error, resultsTruncated, refetch } = useSitesWithinRadius(
    center,
    radiusMeters,
    isLoggedIn,
    enabled
  );

  const userCoordinate: Coordinate | null = resolvedLocationRegion
    ? { latitude: resolvedLocationRegion.latitude, longitude: resolvedLocationRegion.longitude }
    : null;

  return {
    mapRegion,
    setMapRegion,
    sites,
    loading,
    error,
    resultsTruncated,
    refetch,
    recenter: wrappedRecenter,
    userCoordinate,
  };
}
